import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { App, Button, Card, Flex, Form, FormProps, Input, Modal, Popover, Select, Typography, Upload } from "antd"
import { ReactNode, useEffect, useRef, useState } from "react";
import { APIService } from "../services/API.service";
import { CloseOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { Audience, AudienceFilterType, Campaign, CreateAudience, CreateAudienceGroupFilter } from "../interfaces/Campaign.interface";
import { DefaultOptionType } from "antd/es/select";
import { toTitleCase } from "../utils/string.utils"
import { Tag, TagValueType } from "../interfaces/Tag.interface";
import { useAuth } from "../hooks/useAuth";
import { Mode } from "../interfaces/mode.type";
import { RcFile, UploadChangeParam, UploadFile } from "antd/es/upload";
import { stringToBase64 } from "../utils/file.utils";
import { base64 } from "@firebase/util";

const InputElement = (props:{fieldName: string, filterType: AudienceFilterType, tags: Tag[], value?: string|string[]|number|undefined, onChange?:(value:string|string[]|number|undefined)=>void, }) => {
    const { message } = App.useApp();
    let tag = props.tags.find(t => t.name == props.fieldName);
    const [value, setValue] = useState<string|string[]|number|undefined>(props.value);

    useEffect(()=>{
        if(props.value)
        {
            setValue(props.value);
        }
    },[props.value])

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setNotifyValue(e.target.value);
      };

      
    const setNotifyValue = (value:string|string[]|number|undefined)=> {
        setValue(value);
        if(props.onChange)
        {
            props?.onChange(value);
        }
    }
    
    switch (props.filterType) {
        case AudienceFilterType.STR_IS_ONE_OF:
        case AudienceFilterType.STR_IS_NOT_ONE_OF:
            return (
            <Select
                value={value}
                onChange={(value:any, option)=>{
                    if(typeof value == "string")
                    {
                        value = value.trim();
                    } else if (typeof value == "object" && Array.isArray(value))
                    {
                        value = value.map(x=>x.trim());
                    }
                    setNotifyValue(value);
                }}
                mode={tag?.meta.allowedValues ? "multiple" : "tags"}
                options={tag?.meta.allowedValues ?? []}
                filterOption={(input, option)=>{
                    if(option)
                    {
                        return option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0 || 
                            option?.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    return false

                }}
            ></Select>
            )
            break;
        default:
            return (<Input value={value} onChange={onInputChange}></Input>)
            break;
    }
}

const FilterFieldItem = (props: {
    filterField: any,
    field: any,
    form: any,
    tagQuery: any,
    auth: any,
    applicationId: string,
    audienceId: string,
    remove: (index: number) => void
}) => {
    const { filterField, field, form, tagQuery, auth, applicationId, audienceId, remove } = props;
    
    const fieldNameWatch = Form.useWatch(
        ["audienceGroups", field.name, "filters", filterField.name, "fieldName"],
        form
    );
    
    const filterTypeWatch = Form.useWatch(
        ["audienceGroups", field.name, "filters", filterField.name, "type"],
        form
    );
    
    const fileWatch = Form.useWatch(
        ["audienceGroups", field.name, "filters", filterField.name, "file_dataUri"],
        form
    );
    
    const existingFileWatch = Form.useWatch(
        ["audienceGroups", field.name, "filters", filterField.name, "file"],
        form
    );
    
    // Determine which file data to use (uploaded file or existing file from server)
    const activeFile = existingFileWatch || fileWatch;
    
    return (
        <div key={filterField.key}>
            <Flex vertical={false} gap={2} align="baseline">
                <Form.Item 
                    label={null} 
                    name={[filterField.name, 'fieldName']} 
                    style={{ width: "20%" }}
                    rules={[{ required: true, message: "Field is required" }]}
                >
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select field"
                        options={(tagQuery.data ?? []).map((x: any) => ({
                            label: x.label,
                            value: x.name
                        }))} 
                    />
                </Form.Item>

                <Form.Item 
                    label={null} 
                    name={[filterField.name, 'type']} 
                    style={{ width: "30%" }} 
                    rules={[{ required: true, message: "Filter type is required" }]}
                >
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select filter type"
                        options={filterTypeToOptions(fieldNameWatch, tagQuery.data ?? [])} 
                    />
                </Form.Item>
                
                {((typeof activeFile === 'string' ? false : !activeFile?.name && !activeFile?.uid && !activeFile?.id) && ![AudienceFilterType.BOOL_IS_FALSE,AudienceFilterType.BOOL_IS_TRUE].includes(filterTypeWatch)) && 
                <Form.Item 
                    label={null} 
                    name={[filterField.name, 'string_value']} 
                    style={{ minWidth: "40%" }} 
                    rules={[
                        { 
                            required: true, 
                            message: "Filter value is required" 
                        },
                        {
                            validator: (_, value) => {
                                const tag = (tagQuery.data ?? []).find((t: any) => t.name === fieldNameWatch);
                                
                                // Validate based on tag type
                                if (tag?.type === TagValueType.Number && filterTypeWatch?.includes('NUM')) {
                                    if (isNaN(Number(value))) {
                                        return Promise.reject("Value must be a valid number");
                                    }
                                }
                                
                                // Validate allowed values
                                if (tag?.meta?.allowedValues && [AudienceFilterType.STR_IS_ONE_OF, AudienceFilterType.STR_IS_NOT_ONE_OF].includes(filterTypeWatch)) {
                                    const allowedValuesList = tag.meta.allowedValues.map((av: any) => av.value);
                                    const values = Array.isArray(value) ? value : [value];
                                    const invalidValues = values.filter(v => !allowedValuesList.includes(v));
                                    if (invalidValues.length > 0) {
                                        return Promise.reject(`Invalid values: ${invalidValues.join(', ')}. Allowed values are: ${allowedValuesList.join(', ')}`);
                                    }
                                }
                                
                                return Promise.resolve();
                            }
                        }
                    ]}
                >
                    <InputElement 
                        fieldName={fieldNameWatch} 
                        filterType={filterTypeWatch} 
                        tags={tagQuery.data ?? []} 
                    />
                </Form.Item>}
                
                {(activeFile && typeof activeFile === 'object' && 'id' in activeFile && ![AudienceFilterType.BOOL_IS_FALSE,AudienceFilterType.BOOL_IS_TRUE].includes(filterTypeWatch)) && 
                <Button icon={<DownloadOutlined />} onClick={async ()=>{ 
                    await auth.api.downloadAudienceFile(applicationId, audienceId, activeFile.id, activeFile.fileName)
                }}>
                    {activeFile.fileName}
                </Button>}

                <Form.Item 
                    label={null} 
                    name={[filterField.name, 'file_dataUri']} 
                    valuePropName="file" 
                    getValueFromEvent={(event) => {
                        return event?.file
                    }}
                    style={{ width: "10%" }}
                    rules={[
                        {
                            validator: (_, file) => {
                                if (!file) {
                                    return Promise.resolve();
                                }
                                
                                const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
                                if (!isCSV) {
                                    return Promise.reject('Please upload a CSV file');
                                }
                                
                                const isLt10M = file.size / 1024 / 1024 < 10;
                                if (!isLt10M) {
                                    return Promise.reject('File must be smaller than 10MB');
                                }
                                
                                return Promise.resolve();
                            }
                        }
                    ]}
                >
                    <Upload
                        maxCount={1}
                        beforeUpload={() => false}
                        accept=".csv"
                    >
                        <Button icon={<UploadOutlined />}>CSV</Button>
                    </Upload>
                </Form.Item>

                {/* Hidden field to preserve existing file reference when editing */}
                <Form.Item 
                    hidden
                    name={[filterField.name, 'file']}
                    preserve
                >
                    <Input />
                </Form.Item>

                <CloseOutlined
                    onClick={() => {
                        // Manual array management to prevent clearing fields below
                        const allValues = form.getFieldsValue();
                        const currentFilters = allValues.audienceGroups?.[field.name]?.filters || [];
                        const newFilters = currentFilters.filter((_: any, idx: number) => idx !== filterField.name);
                        
                        const newGroups = [...(allValues.audienceGroups || [])];
                        if (newGroups[field.name]) {
                            newGroups[field.name] = {
                                ...newGroups[field.name],
                                filters: newFilters
                            };
                        }
                        form.setFieldsValue({ audienceGroups: newGroups });
                    }}
                />
            </Flex>
        </div>
    );
};

const filterTypeToOptions = (fieldName: string, tags: Tag[]): DefaultOptionType[] => {
    let tag = tags.find(t => t.name == fieldName);
    let typePrefix = "BOOL";
    switch (tag?.type) {
        case TagValueType.String:
            typePrefix = "STR"
            break;
        case TagValueType.Number:
            typePrefix = "NUM"
            break;
        case TagValueType.Boolean:
            typePrefix = "BOOL"
            break;
        case TagValueType.Enum_String:
            typePrefix = "STR"
            break;
    }

    let result =  Object.keys(AudienceFilterType).filter(x=>{

        if(tag?.meta.allowedValues)
        {
            return [AudienceFilterType.STR_IS_ONE_OF.toString(), 
                AudienceFilterType.STR_IS_NOT_ONE_OF.toString()].includes(x);
        }
        return true

    }).filter(type => type.startsWith(typePrefix)).map(type => {
        let label = type;
        label = label.replace("STR", "").replace("BOOL", "").replace("NUM", "");
        label = label.split("_").filter(x => x.length > 0).map(x => toTitleCase(x)).join(" ")
        return {
            label: label,
            value: type
        };
    });

    return result;
}

export interface AudienceCreatorProps {
    operationMode:Mode;
    applicationId: string,
    saveAudience: (audience:CreateAudience)=>Promise<void>;
    onClose?:()=>void;
    initial?:Audience
}

export const AudienceCreator = (props: AudienceCreatorProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const auth = useAuth();

    // Access the client
    const queryClient = useQueryClient()

    // Queries
    const tagQuery = useQuery({ queryKey: ['tags', props.applicationId], queryFn: () => auth.api.getTagsForApp(props.applicationId) })

    // Helper function to parse server errors
    const parseServerError = (error: any) => {
        let message = "An unexpected error occurred";
        let fieldErrors = null;
        
        if (error?.response?.data?.fieldErrors) {
            fieldErrors = error.response.data.fieldErrors;
        }
        
        // Extract validation errors from context (for class-validator errors)
        if (error?.response?.data?.context && Array.isArray(error.response.data.context)) {
            const extractConstraintMessages = (obj: any): string[] => {
                let messages: string[] = [];
                
                if (obj?.constraints) {
                    messages.push(...Object.values(obj.constraints).map(v => String(v)));
                }
                
                if (obj?.children && Array.isArray(obj.children)) {
                    for (const child of obj.children) {
                        messages.push(...extractConstraintMessages(child));
                    }
                }
                
                return messages;
            };
            
            const allErrorMessages: string[] = [];
            for (const contextItem of error.response.data.context) {
                allErrorMessages.push(...extractConstraintMessages(contextItem));
            }
            
            if (allErrorMessages.length > 0) {
                message = allErrorMessages[0]; // Use the first specific error message
            }
        }
        
        // Fallback to generic message extraction
        if (message === "An unexpected error occurred") {
            if (error?.response?.data?.message) {
                message = error.response.data.message;
            } else if (error?.response?.data?.error) {
                message = error.response.data.error;
            } else if (error?.message) {
                message = error.message;
            }
        }
        
        return { message, fieldErrors };
    };

    const validateFilters = (filters: { fieldName?: string; type?: string }[]) => {
        return filters.some(filter => filter.fieldName && filter.type);
    };

    const validateAudienceGroups = (groups: { filters: { fieldName?: string; type?: string }[] }[]) => {
        return groups.every(group => validateFilters(group.filters));
    };

    const handleSave = async () => {
        try {
            // Validate form first
            await form.validateFields();
            
            let data = form.getFieldsValue();
            
            // Validate that each audience group has at least one valid filter
            if (!validateAudienceGroups(data.audienceGroups)) {
                message.error("Each filter group must contain at least one valid filter.");
                return;
            }
            
            data = cleanseData(data);
            
            if(data.audienceGroups){
            data.audienceGroups = await Promise.all(data.audienceGroups?.map(async (group:any)=>{
                group.filters = await Promise.all(group.filters.map(async (filter:any)=>{
                    if(filter.string_value && Array.isArray(filter.string_value)) {
                        filter.string_value = filter.string_value.join(',')
                    }
                    
                    // Handle newly uploaded files (file_dataUri is a File object)
                    if(filter.file_dataUri && typeof filter.file_dataUri != "string")
                    {   
                        // Get the actual File object - could be in originFileObj or directly
                        const fileObj = filter.file_dataUri.originFileObj || filter.file_dataUri;
                        let text = "";
                        
                        try {
                            if (fileObj && typeof fileObj.text === 'function') {
                                text = await fileObj.text();
                            } else if (fileObj instanceof File || fileObj instanceof Blob) {
                                text = await new Promise((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = (e) => resolve(e.target?.result as string || "");
                                    reader.onerror = (e) => reject(new Error("Failed to read file"));
                                    reader.readAsText(fileObj);
                                });
                            } else {
                                throw new Error("Invalid file object structure");
                            }
                            
                            if (!text || text.trim() === "") {
                                throw new Error("CSV file is empty or could not be read");
                            }
                        } catch (error) {
                            console.error("File reading error:", error, "File object:", filter.file_dataUri);
                            throw new Error(`Failed to read CSV file: ${(error as Error).message}`);
                        }
                        
                        filter.fileName = `${filter.file_dataUri.name || fileObj.name}`;
                        filter.file_dataUri = `data:text/csv;base64,${stringToBase64(text)}`;
                        filter.string_value = undefined;
                        if(![AudienceFilterType.STR_IS_NOT_ONE_OF, AudienceFilterType.STR_IS_ONE_OF].includes(filter.type))
                        {
                            filter.type = AudienceFilterType.STR_IS_ONE_OF
                        }
                        delete (filter as any).fileId;
                        delete (filter as any).file;
                    } 
                    // Handle existing files (file exists on server, need to download as base64)
                    else if(filter.file)
                    {
                        let fileName = filter.file.fileName;
                        let dataUri = await auth.api.downloadAudienceFileAsBase64(props.applicationId, props.initial?.id ?? "unknown-file-audience-id", filter.file.id, filter.file.fileName);
                        filter.file_dataUri = dataUri;
                        filter.fileName = fileName;
                        delete filter.file;
                        delete filter.fileId;
                    } 
                    // No file uploaded
                    else {
                        delete filter.file;
                        delete filter.fileId;
                        if(!filter.file_dataUri) {
                            filter.fileName = undefined; 
                        }
                    }
                    return filter;
                }))
                return group;
            }))
        }

        if(props.operationMode == Mode.Clone) {
            data.id = undefined;
        }

        if(props.saveAudience) {
            setConfirmLoading(true)
            await props.saveAudience(data);
            setConfirmLoading(false)
            setIsOpen(false);
            message.success("Segment saved successfully");
        }

        if(props.onClose) {
            props.onClose();
        }
        } catch (error: any) {
            setConfirmLoading(false);
            
            // Check if it's a form validation error
            if (error.errorFields && Array.isArray(error.errorFields)) {
                // Extract and show specific validation error messages
                const errorMessages = error.errorFields
                    .map((field: any) => field.errors)
                    .flat()
                    .filter((msg: string, index: number, arr: string[]) => arr.indexOf(msg) === index); // Remove duplicates
                
                errorMessages.forEach((msg: string) => {
                    message.error(msg);
                });
                return;
            }
            // Parse and display server error
            const { message: errorMessage, fieldErrors } = parseServerError(error);
            message.error(errorMessage);
            
            if (fieldErrors) {
                form.setFields(Object.keys(fieldErrors).map(field => ({
                    name: field,
                    errors: [fieldErrors[field]]
                })));
            }
        }
    }

    const handleCancel = () => {
        setIsOpen(false)
        if(props.onClose)
        {
            props.onClose();
        }
    }

    const createAudience = () => {
        setIsOpen(true)
    }


    const cleanseData = (data: CreateAudience) => {
        if (data.audienceGroups) {
            data.audienceGroups = data.audienceGroups.map((group: any) => {
                group.filters = group.filters.map((filter: any) => {
                    if (![AudienceFilterType.STR_IS_ONE_OF.toString(), AudienceFilterType.STR_IS_NOT_ONE_OF.toString()].includes(filter.type) && filter.string_value && Array.isArray(filter.string_value)) {
                        filter.string_value = filter.string_value?.join(",");
                    }
                    else if (([AudienceFilterType.STR_IS_ONE_OF.toString(), AudienceFilterType.STR_IS_NOT_ONE_OF.toString()].includes(filter.type) && filter.string_value && !Array.isArray(filter.string_value))) {
                        let tag = (tagQuery.data ?? []).find(x => x.name == filter.fieldName);
                        let allowedValues = tag?.meta.allowedValues ?? null;
                        filter.string_value = filter.string_value?.split(",").filter((x: string) => x.length > 0).filter((x: string) => {

                            if (allowedValues != null) {
                                if (!allowedValues?.map(av => av.value).includes(x)) {
                                    return null;
                                }
                            }
                            return x;
                        }).filter((v: null | string) => v).join(',');
                    }
                    return filter;
                })
                return group;
            })
        }
        return data;
    }

    const onFinish: FormProps<any>['onFinish'] = (values) => {
        handleSave();
    };

    const onFinishFailed: FormProps<any>['onFinishFailed'] = (errorInfo) => {
        message.error("Please fix the validation errors before saving");
    };

    useEffect(()=>{
        if(props.initial)
        {
            setIsOpen(true);
            // Always work on a cloned object so cleanseData can't mutate props.initial
            let values = JSON.parse(JSON.stringify(props.initial));
            if(props.operationMode == Mode.Clone)
            {
                values.name = `${props.initial?.name} Copy`
            }
            const normalizedValues = (values as unknown as CreateAudience);
            
            // Convert comma-separated strings to arrays for multi-select filters (for UI display)
            if (normalizedValues.audienceGroups) {
                normalizedValues.audienceGroups = normalizedValues.audienceGroups.map((group: any) => {
                    group.filters = group.filters.map((filter: any) => {
                        if ([AudienceFilterType.STR_IS_ONE_OF.toString(), AudienceFilterType.STR_IS_NOT_ONE_OF.toString()].includes(filter.type) 
                            && filter.string_value 
                            && typeof filter.string_value === 'string') {
                            // Split comma-separated string into array for Select component
                            filter.string_value = filter.string_value.split(',').map((x: string) => x.trim()).filter((x: string) => x.length > 0);
                        }
                        return filter;
                    });
                    return group;
                });
            }
            
            form.setFieldsValue(normalizedValues as unknown as CreateAudience);
        }
    }, [props.initial])

    let title = "Create";
    switch(props.operationMode)
    {
        case Mode.New:
            title = "Create";
            break;
        case Mode.Clone:
            title = "Clone";
            break;
        case Mode.Edit:
            title = "Edit"
            break;
    }
    return (
        <>
            <Button type="primary" onClick={createAudience}>
                <PlusOutlined /> New Segment
            </Button>
            <Modal
                title={`${title} Segment`}
                open={isOpen}
                onOk={handleSave}
                okText={"Save"}
                confirmLoading={confirmLoading}
                onCancel={handleCancel}
                width={`99vw`}
                destroyOnClose
            >
                <Form
                    preserve={false}
                    layout={"horizontal"}
                    form={form}
                    initialValues={{}}
                    // onValuesChange={null}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <Form.Item 
                        label="Name"
                        name={"name"}
                        rules={[
                            { required: true, message: "Segment name is required" },
                            {
                                validator: (_, value) => {
                                    if (props.operationMode == Mode.Clone && value == props.initial?.name) {
                                        return Promise.reject("Can't clone a segment and save it with the original name");
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input placeholder="Segment Name" disabled={props.operationMode == Mode.Edit} />
                    </Form.Item>
                    
                    <Form.List 
                        name="audienceGroups"
                        rules={[
                            {
                                validator: async (_, audienceGroups) => {
                                    if (!audienceGroups || audienceGroups.length === 0) {
                                        return Promise.reject("At least one filter group is required");
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        {(fields, { add, remove }, { errors }) => (
                            <div style={{ display: 'flex', rowGap: 16, flexDirection: 'column' }}>
                                {fields.map((field) => (
                                    <Card
                                        title={`Segment Filter Group (${form.getFieldValue(["audienceGroups", field.name])?.type})`}
                                        size="small"
                                        bordered
                                        key={field.key}
                                        extra={
                                            <CloseOutlined
                                                onClick={() => {
                                                    // Manual array management to prevent clearing groups below
                                                    const allValues = form.getFieldsValue();
                                                    const currentGroups = allValues.audienceGroups || [];
                                                    const newGroups = currentGroups.filter((_: any, idx: number) => idx !== field.name);
                                                    form.setFieldsValue({ audienceGroups: newGroups });
                                                }}
                                            />
                                        }
                                    >
                                        <Form.Item 
                                            label="Type" 
                                            name={[field.name, 'type']}
                                            rules={[{ required: true, message: "Filter group type is required" }]}
                                        >
                                            <Select
                                                style={{ width: '100%' }}
                                                placeholder="Select group type"
                                                options={[
                                                    { label: "AND", value: "AND" },
                                                    { label: "OR", value: "OR" }
                                                ]} 
                                            />
                                        </Form.Item>
                                        
                                        <Form.List 
                                            name={[field.name, "filters"]}
                                            rules={[
                                                {
                                                    validator: async (_, filters) => {
                                                        if (!filters || filters.length === 0) {
                                                            return Promise.reject("At least one filter is required in each group");
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                        >
                                            {(filterFields, { add, remove }, { errors: filterErrors }) => (
                                                <div style={{ display: 'flex', rowGap: 16, flexDirection: 'column' }}>
                                                    {filterFields.map((filterField) => (
                                                        <FilterFieldItem
                                                            key={filterField.key}
                                                            filterField={filterField}
                                                            field={field}
                                                            form={form}
                                                            tagQuery={tagQuery}
                                                            auth={auth}
                                                            applicationId={props.applicationId}
                                                            audienceId={props.initial?.id ?? "unknown-audience-id"}
                                                            remove={remove}
                                                        />
                                                    ))}
                                                    <div style={{ color: '#ff4d4f', fontSize: '14px', marginTop: '4px' }}>
                                                        {filterErrors.map((error, index) => (
                                                            <div key={index}>{error}</div>
                                                        ))}
                                                    </div>
                                                    <Flex vertical={true} gap={5}>
                                                        <Button onClick={() => add({ fieldName: (tagQuery.data ?? []).find(x=>x)?.name, type: AudienceFilterType.STR_EQUALS})}> <PlusOutlined /> New Filter</Button>
                                                    </Flex>
                                                </div>
                                            )}
                                        </Form.List>
                                    </Card>
                                ))}

                                <div style={{ color: '#ff4d4f', fontSize: '14px', marginTop: '4px' }}>
                                    {errors.map((error, index) => (
                                        <div key={index}>{error}</div>
                                    ))}
                                </div>
                                <Flex vertical={true} gap={5}>
                                    {(tagQuery.data ?? []).map(tag => {
                                        let filter: CreateAudienceGroupFilter = {
                                            type: AudienceFilterType.STR_EQUALS,
                                            fieldName: tag.name,
                                            string_value: "Some value"
                                        }

                                        switch (tag.type) {
                                            case TagValueType.Number:
                                                filter = {
                                                    type: AudienceFilterType.NUM_EQUAL_TO,
                                                    fieldName: tag.name,
                                                    num_value: 0
                                                }
                                                break;
                                            case TagValueType.Boolean:
                                                filter = {
                                                    type: AudienceFilterType.BOOL_IS_TRUE,
                                                    fieldName: tag.name,
                                                    num_value: 0
                                                }
                                                break;
                                        }
                                        if(tag.meta.allowedValues){
                                            filter.type = AudienceFilterType.STR_IS_ONE_OF
                                        }
                                        return (
                                            <Button key={tag.id} type="dashed" onClick={() => add({
                                                type: "AND",
                                                filters: [filter]
                                            })} block>
                                                + {tag.label}
                                            </Button>
                                        )
                                    })}
                                </Flex>
                            </div>
                        )}
                    </Form.List>
                </Form>
            </Modal>
        </>
    )
}
